// @givmo/app-admin — the per-status employee-invite counts, from the endpoint that exists for them
// (EP-BE12), with the old client-side derivation kept as a fallback.
//
// ── WHY THIS IS TWO READS AND NOT ONE ────────────────────────────────────────────────────────────
//
// `GET /admin/company/invites/counts` is the durable fix for a seam `agent_docs/api-client.md` calls
// out by name: the invites LIST stamps its `counts` beside `data`, and the response middleware discards
// every sibling of `data`, so the number was unreachable from web and the surface had to page the whole
// list and add the rows up. The dedicated route answers a PLAIN object — no envelope, nothing to strip.
//
// But that route SHIPS WITH THE BACKEND, and this app deploys on its own cadence: a build carrying this
// code reaches staging and prod before the backend carrying the route does, and 404s it until the next
// promotion round. A hard switch would blank every chip on the Invites tab and drop the dashboard's
// pending-invite figure for the whole interval. So the read is additive-tolerant in BOTH directions —
// prefer the endpoint, fall back to the derivation — which is the same posture the occasion-dates roster
// latch takes toward its `error_code` (`occasion-dates-decode.ts`). The fallback is the reason
// `fetchAllInvites` still exists for counting at all; delete it and an old backend silently loses count.
//
// The fallback is lazy — `enabled` only once the endpoint read has actually FAILED — so against a
// current backend this costs exactly one cheap request and pages nothing. It also shares
// `INVITES_ALL_KEY` with the Invites tab's own row list, so on that surface the fallback resolves to the
// cache entry the rows already populated rather than fetching the list a second time.
//
// A count is `null`, never 0, while unknown: "0 pending" when we simply could not count is a claim the
// client cannot make. Callers that must render a number regardless use `EMPTY_INVITE_COUNTS`.

import { useQuery } from '@tanstack/react-query';
import { companiesGetCompanyEmployeeInviteCountsOptions } from '@givmo/api-client/query';
import {
  fetchAllInvites,
  inviteCounts,
  INVITES_ALL_KEY,
  toInviteCounts,
  type InviteCounts,
} from './invites-decode';

export interface InviteCountsRead {
  /** The counts, or `null` while neither source has answered — never a fabricated zero. */
  counts: InviteCounts | null;
  /** A read is still in flight (the endpoint, or the fallback it handed off to). */
  isPending: boolean;
  /** BOTH sources failed. The endpoint failing alone is the expected pre-promotion state, not an error. */
  isError: boolean;
}

export function useInviteCounts(): InviteCountsRead {
  const endpoint = useQuery(companiesGetCompanyEmployeeInviteCountsOptions());

  // Only after the endpoint has given up — an older backend 404s it, and that must degrade to the
  // derivation rather than to a blank summary row.
  const fallback = useQuery({
    queryKey: INVITES_ALL_KEY,
    queryFn: ({ signal }) => fetchAllInvites(undefined, signal),
    enabled: endpoint.isError,
  });

  if (endpoint.isSuccess)
    return { counts: toInviteCounts(endpoint.data), isPending: false, isError: false };
  if (endpoint.isError) {
    if (fallback.isSuccess) {
      return { counts: inviteCounts(fallback.data), isPending: false, isError: false };
    }
    return { counts: null, isPending: !fallback.isError, isError: fallback.isError };
  }
  return { counts: null, isPending: true, isError: false };
}
